import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { createAnonymousSession, getMe } from "@/api/session";
import type { AnonymousMe } from "@/types";

export const SESSION_QUERY_KEY = ["session", "me"] as const;

/**
 * Récupère l'identité courante. Le backend H7-6 répond désormais 200
 * ``null`` si pas de session (au lieu de 401), donc plus aucune erreur
 * XHR n'est émise au boot. Si ``null``, on bootstrappe via
 * `POST /sessions/anonymous` puis on relit `/sessions/me`.
 *
 * Le fallback historique sur 401 reste en place pour couvrir un backend
 * non-déployé H7-6 (rétrocompat opérationnelle) et tout autre 401
 * imprévu sur cet endpoint.
 *
 * Toute autre erreur est propagée telle quelle au cache TanStack Query.
 */
export async function getOrCreateMe(): Promise<AnonymousMe> {
  try {
    const me = await getMe();
    if (me !== null) {
      return me;
    }
    // H7-6 : 200 null → pas de session active, on bootstrappe.
    await createAnonymousSession();
    const created = await getMe();
    if (created === null) {
      throw new Error(
        "Bootstrap session : POST /sessions/anonymous ne pose pas de cookie.",
      );
    }
    return created;
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 401 || err.code === "ANONYMOUS_SESSION_REQUIRED")
    ) {
      // Compat rétrograde (backend pré-H7-6) — silencer le 401.
      await createAnonymousSession();
      const created = await getMe();
      if (created === null) {
        throw new Error(
          "Bootstrap session : POST /sessions/anonymous ne pose pas de cookie.",
        );
      }
      return created;
    }
    throw err;
  }
}

/**
 * Hook unique d'accès à la session anonyme SunMatch.
 *
 * Au premier appel `useSession`, on tente `GET /api/v1/sessions/me` :
 * - si le cookie httpOnly `sunmatch_anon` est déjà posé, on récupère
 *   directement l'identité ;
 * - sinon, le backend renvoie 401 et on déclenche `POST /sessions/anonymous`
 *   avant de relire `/sessions/me`.
 *
 * Lecture seule : consentement / suppression de compte passent par leurs
 * hooks dédiés (`postSessionConsent`, `useDeleteMyData`).
 */
export function useSession(): {
  me: AnonymousMe | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: getOrCreateMe,
    // L'identité ne change quasiment jamais en runtime — pas besoin de
    // re-fetch fréquent.
    staleTime: 5 * 60_000,
    retry: false,
  });

  return {
    me: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  };
}
